import { useEffect, useRef } from 'react';

const PARTICLES = [
  { left: '5%',  delay: 0,    duration: 3.2 },
  { left: '13%', delay: 0.9,  duration: 4.1 },
  { left: '22%', delay: 1.7,  duration: 3.6 },
  { left: '31%', delay: 0.4,  duration: 4.8 },
  { left: '40%', delay: 2.2,  duration: 3.4 },
  { left: '49%', delay: 1.2,  duration: 4.3 },
  { left: '58%', delay: 0.7,  duration: 3.9 },
  { left: '67%', delay: 2.0,  duration: 4.6 },
  { left: '76%', delay: 0.5,  duration: 3.1 },
  { left: '84%', delay: 2.5,  duration: 4.0 },
  { left: '91%', delay: 1.4,  duration: 3.7 },
  { left: '96%', delay: 0.2,  duration: 4.4 },
];

const GRADIENT = 'linear-gradient(120deg, #0a1828 0%, #1a3a5c 6%, #2a5a8c 12%, #3a7ab8 18%, #5b9fd4 22%, #7bbfe8 25%, #5b9fd4 28%, #2a5a8c 34%, #0a1828 44%, #1a3a5c 54%, #2a5a8c 62%, #3a7ab8 68%, #5b9fd4 71%, #7bbfe8 73%, #5b9fd4 75%, #2a5a8c 80%, #0a1828 88%, #1a3a5c 94%, #0a1828 100%)';

export default function EmberTicker() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Film grain via canvas — drawn directly, no blend mode tricks
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let frame: number;
    let tick = 0;

    const draw = () => {
      tick++;
      // Only redraw every 3 frames so grain "shifts" visibly
      if (tick % 3 === 0) {
        const w = canvas.width;
        const h = canvas.height;
        const imageData = ctx.createImageData(w, h);
        const data = imageData.data;
        for (let i = 0; i < data.length; i += 4) {
          const v = Math.random() > 0.5 ? 255 : 0;
          data[i]     = v;
          data[i + 1] = v;
          data[i + 2] = v;
          data[i + 3] = Math.random() * 18; // subtle but real
        }
        ctx.putImageData(imageData, 0, 0);
      }
      frame = requestAnimationFrame(draw);
    };

    const resize = () => {
      if (!canvas) return;
      canvas.width  = canvas.offsetWidth  || 1200;
      canvas.height = canvas.offsetHeight || 32;
    };

    resize();
    draw();
    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <>
      {/* Breathing wrapper — opacity pulses on the whole bar */}
      <div
        ref={wrapperRef}
        style={{ animation: 'ticker-breathe 6s ease-in-out infinite' }}
      >
        {/* TICKER BAR */}
        <div
          className="relative overflow-visible py-1.5"
          style={{
            background: GRADIENT,
            backgroundSize: '300% 100%',
            animation: 'water-flow 24s linear infinite',
            zIndex: 50,
          }}
        >
          {/* Film grain canvas — screen blend so it lightens grain on dark bg */}
          <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{
              zIndex: 10,
              mixBlendMode: 'screen',
              opacity: 0.55,
            }}
          />

          {/* Floating dust particles — overflow-visible so they escape the bar */}
          {PARTICLES.map((p, i) => (
            <div
              key={i}
              className="absolute pointer-events-none rounded-full"
              style={{
                left: p.left,
                bottom: '2px',
                width: '1.5px',
                height: '1.5px',
                background: 'rgba(160, 210, 255, 0.9)',
                boxShadow: '0 0 3px 1px rgba(120, 190, 255, 0.6)',
                animation: `ticker-particle-rise ${p.duration}s ease-out ${p.delay}s infinite`,
                zIndex: 20,
              }}
            />
          ))}

          {/* Scrolling marquee — two identical copies for seamless infinite loop */}
          <div className="relative z-20 overflow-hidden w-full flex">
            {/* Copy A */}
            <div className="animate-marquee whitespace-nowrap flex-shrink-0" aria-hidden="false">
              {[...Array(8)].map((_, i) => (
                <span
                  key={i}
                  className="text-white/90 text-xs font-semibold uppercase mx-16"
                  style={{ letterSpacing: '0.35em' }}
                >
                  OPEN FOR NEW PROJECTS
                </span>
              ))}
            </div>
            {/* Copy B — starts immediately after A, creates the seamless loop */}
            <div className="animate-marquee whitespace-nowrap flex-shrink-0" aria-hidden="true">
              {[...Array(8)].map((_, i) => (
                <span
                  key={i}
                  className="text-white/90 text-xs font-semibold uppercase mx-16"
                  style={{ letterSpacing: '0.35em' }}
                >
                  OPEN FOR NEW PROJECTS
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Accent divider */}
      <div
        className="w-full"
        style={{
          height: '2px',
          background: GRADIENT,
          backgroundSize: '300% 100%',
          animation: 'water-flow 24s linear infinite',
          opacity: 0.6,
        }}
      />
    </>
  );
}
